import React from "react";
import { Empty, Spin } from "antd";
import { CopyOutlined } from "@ant-design/icons";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneLight } from "react-syntax-highlighter/dist/esm/styles/prism";
import clsx from "clsx";
import copyToClipboard from "copy-to-clipboard";
import type { FolderTreeData, FolderTreeProps } from "./types";
import { useStyles } from "./styles";

export interface FilePreviewProps {
  selectedFile?: string[] | null;
  fileContent?: string;
  loading?: boolean;
  previewTitle?: FolderTreeProps["previewTitle"];
  previewRender?: FolderTreeProps["previewRender"];
  emptyRender?: FolderTreeProps["emptyRender"];
  getFileNode?: (
    path: string[]
  ) =>
    | { title: FolderTreeData["title"]; path: string; content?: string }
    | undefined;
}

const customOneLight = {
  ...oneLight,
  'pre[class*="language-"]': {
    ...oneLight['pre[class*="language-"]'],
    margin: 0,
    background: "transparent",
    padding: 0,
    borderRadius: 0,
  },
};

const getFileExtension = (path = "") => {
  const parts = path.split(".");
  return parts[parts.length - 1] || "";
};

const EXTENSION_LANGUAGE_MAP: Record<string, string> = {
  ts: "typescript",
  tsx: "tsx",
  js: "javascript",
  jsx: "jsx",
  md: "markdown",
  json: "json",
  css: "css",
  scss: "scss",
  less: "less",
  html: "html",
  xml: "xml",
  yaml: "yaml",
  yml: "yaml",
  py: "python",
  java: "java",
  go: "go",
  rs: "rust",
  sh: "bash",
  bash: "bash",
  sql: "sql",
  graphql: "graphql",
  vue: "html",
  svelte: "html",
};

const getLanguageFromExtension = (ext: string) => {
  const lower = ext.toLowerCase();
  return EXTENSION_LANGUAGE_MAP[lower] || lower || "text";
};

const FilePreview: React.FC<FilePreviewProps> = ({
  selectedFile,
  fileContent = "",
  loading = false,
  previewTitle,
  previewRender,
  emptyRender,
  getFileNode,
}) => {
  const styles = useStyles();

  if (loading) {
    return (
      <div className={clsx(styles.previewPanel)}>
        <div className={styles.loadingContainer}>
          <Spin />
        </div>
      </div>
    );
  }

  if (!selectedFile || selectedFile.length === 0) {
    if (emptyRender === false || emptyRender === null) {
      return <div className={styles.previewPanel} />;
    }
    const emptyNode =
      typeof emptyRender === "function"
        ? emptyRender()
        : emptyRender || (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description="请选择文件查看内容"
            />
          );
    return (
      <div className={styles.previewPanel}>
        <div className={styles.emptyContainer}>{emptyNode}</div>
      </div>
    );
  }

  const fileNode = getFileNode?.(selectedFile);
  const title = fileNode?.title || selectedFile[selectedFile.length - 1];
  const fileName = selectedFile[selectedFile.length - 1];
  const extension = getFileExtension(fileName);
  const language = getLanguageFromExtension(extension);

  // 标题区域
  let headerNode: React.ReactNode;
  if (previewTitle === false || previewTitle === null) {
    headerNode = null;
  } else if (previewTitle) {
    headerNode =
      typeof previewTitle === "function"
        ? previewTitle({ title, path: selectedFile, content: fileContent })
        : previewTitle;
  } else {
    headerNode = (
      <div className={styles.previewTitle}>
        <span>{title}</span>
        <CopyOutlined
          className={styles.copyBtn}
          onClick={() => copyToClipboard(fileContent)}
        />
      </div>
    );
  }

  // 内容区域
  const originContentNode = (
    <SyntaxHighlighter
      language={language}
      wrapLines
      style={customOneLight}
      codeTagProps={{ style: { background: "transparent" } }}
    >
      {fileContent.replace(/\n$/, "")}
    </SyntaxHighlighter>
  );

  let contentNode: React.ReactNode;
  if (previewRender) {
    if (typeof previewRender === "function") {
      contentNode = previewRender(
        { content: fileContent, path: selectedFile, title: fileNode?.title, language },
        { originNode: originContentNode }
      );
    } else {
      contentNode = previewRender;
    }
  } else {
    contentNode = originContentNode;
  }

  return (
    <div className={styles.previewPanel}>
      {headerNode}
      <div className={styles.previewContent}>{contentNode}</div>
    </div>
  );
};

export default FilePreview;
